import { CSSProperties } from "react";
import { theme } from "./theme";

export const styles: Record<string, CSSProperties> = {
    overlay: {
        position: "fixed",
        inset: 0,
        zIndex: 4000,
        padding: "0.9rem",
        background: "rgba(15, 23, 42, 0.38)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    dialog: {
        width: "min(1180px, calc(100vw - 1.4rem))",
        maxHeight: "calc(100vh - 1.4rem)",
        minHeight: "min(650px, calc(100vh - 1.4rem))",
        background: theme.bgSurface,
        borderRadius: "8px",
        border: `1px solid ${theme.borderSoft}`,
        boxShadow: theme.shadowSoft,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    header: {
        padding: "1rem 1.18rem 0.82rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "1rem",
        borderBottom: `1px solid ${theme.borderSoft}`,
        background: theme.bgSurface,
    },
    titleWrap: {
        minWidth: 0,
    },
    eyebrow: {
        margin: "0 0 0.35rem",
        color: theme.brand,
        fontSize: "0.78rem",
        fontWeight: 800,
    },
    title: {
        margin: 0,
        color: theme.fgStrong,
        fontSize: "1.22rem",
        fontWeight: 800,
        lineHeight: 1.25,
    },
    closeBtn: {
        border: "none",
        background: "transparent",
        color: theme.fgMuted,
        width: "2.2rem",
        height: "2.2rem",
        borderRadius: "5px",
        fontSize: "1.35rem",
        lineHeight: 1,
        cursor: "pointer",
        flexShrink: 0,
    },
    body: {
        padding: "1rem 1.18rem",
        overflowY: "auto",
        flex: 1,
    },
    slideLayout: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "0.85rem",
        alignItems: "start",
        touchAction: "pan-y",
    },
    visualColumn: {
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "0.7rem",
    },
    textColumn: {
        minWidth: 0,
    },
    mediaGrid: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "0.85rem",
    },
    mediaCard: {
        margin: 0,
        border: `1px solid ${theme.borderSoft}`,
        borderRadius: "6px",
        background: theme.bgSurface,
        overflow: "hidden",
        boxShadow: "none",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        alignItems: "stretch",
    },
    mediaImageWrap: {
        position: "relative",
        minHeight: "clamp(220px, 38vh, 300px)",
        background: theme.bgPanel,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
    mediaImage: {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
    },
    mediaCaption: {
        padding: "0.85rem 0.95rem",
        color: theme.fgStrong,
        fontSize: "0.86rem",
        fontWeight: 700,
        lineHeight: 1.35,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "0.7rem",
        borderLeft: `1px solid ${theme.borderSoft}`,
        background: theme.bgControlMuted,
    },
    mediaCaptionHeader: {
        display: "flex",
        alignItems: "center",
        gap: "0.65rem",
    },
    mediaStepBadge: {
        width: "1.85rem",
        height: "1.85rem",
        borderRadius: "5px",
        background: theme.bgSelectionActive,
        color: theme.brandDeep,
        fontSize: "0.82rem",
        fontWeight: 800,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    mediaTitle: {
        color: theme.fgStrong,
        fontSize: "0.95rem",
        fontWeight: 800,
        lineHeight: 1.3,
    },
    mediaDescription: {
        margin: 0,
        color: theme.fgMuted,
        fontSize: "0.84rem",
        fontWeight: 500,
        lineHeight: 1.55,
    },
    summary: {
        margin: "0 0 0.85rem",
        color: theme.fgMuted,
        fontSize: "0.9rem",
        lineHeight: 1.58,
    },
    flow: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "0.75rem",
        alignItems: "stretch",
        marginBottom: "1rem",
    },
    flowNode: {
        position: "relative",
        border: `1px solid ${theme.borderStrong}`,
        borderRadius: "12px",
        padding: "0.85rem",
        background: theme.bgSurface,
        minHeight: "112px",
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
    },
    nodeIndex: {
        width: "1.75rem",
        height: "1.75rem",
        borderRadius: "999px",
        background: "#f2c94c",
        color: "#3c2a02",
        fontSize: "0.78rem",
        fontWeight: 800,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    nodeTitle: {
        color: theme.fgStrong,
        fontSize: "0.9rem",
        fontWeight: 800,
        lineHeight: 1.3,
    },
    nodeText: {
        color: theme.fgMuted,
        fontSize: "0.8rem",
        lineHeight: 1.45,
    },
    noteBox: {
        border: `1px solid ${theme.borderSoft}`,
        background: theme.bgControlMuted,
        borderRadius: "6px",
        padding: "0.78rem 0.88rem",
        color: theme.fgStrong,
        fontSize: "0.86rem",
        lineHeight: 1.55,
    },
    footer: {
        padding: "0.82rem 1.18rem 0.95rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.8rem",
        borderTop: `1px solid ${theme.borderSoft}`,
        background: theme.bgSurface,
        flexWrap: "wrap",
    },
    dots: {
        display: "flex",
        alignItems: "center",
        gap: "0.38rem",
    },
    dot: {
        width: "0.5rem",
        height: "0.5rem",
        borderRadius: "999px",
        border: "none",
        padding: 0,
        background: theme.borderStrong,
        cursor: "pointer",
        transition: "width 180ms ease, background 180ms ease, transform 180ms ease",
    },
    dotActive: {
        width: "1.35rem",
        height: "0.5rem",
        borderRadius: "999px",
        border: "none",
        padding: 0,
        background: theme.brandDeep,
        cursor: "pointer",
        transition: "width 180ms ease, background 180ms ease, transform 180ms ease",
    },
    buttonRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "0.55rem",
        flexWrap: "wrap",
    },
    secondaryButton: {
        border: `1px solid ${theme.borderStrong}`,
        borderRadius: "6px",
        padding: "0.58rem 0.9rem",
        background: theme.bgControl,
        color: theme.fgStrong,
        fontWeight: 700,
        fontSize: "0.84rem",
        cursor: "pointer",
    },
    secondaryButtonDisabled: {
        border: `1px solid ${theme.borderSoft}`,
        borderRadius: "6px",
        padding: "0.58rem 0.9rem",
        background: theme.bgPanel,
        color: theme.fgMuted,
        fontWeight: 700,
        fontSize: "0.84rem",
        cursor: "default",
        opacity: 0.72,
    },
    primaryButton: {
        border: "none",
        borderRadius: "6px",
        padding: "0.6rem 1rem",
        background: theme.brandDeep,
        color: "#ffffff",
        fontWeight: 800,
        fontSize: "0.84rem",
        cursor: "pointer",
        boxShadow: "0 8px 18px rgba(23, 82, 160, 0.22)",
    },
};
